import React from 'react';
import { Link } from 'react-router-dom';
import Container from '../../components/layout/Container/Container';
import { Row, Col } from '../../components/layout/Grid/Grid';
import Card, { CardBody } from '../../components/ui/Card/Card';
import './Services.css';

const Services = () => {
  const services = [
    {
      id: 1,
      icon: 'fas fa-code',
      title: 'Web Development',
      description: 'Custom web applications built with modern technologies like React, Node.js, and more.',
      features: [
        'Responsive websites',
        'Single Page Applications',
        'E-commerce solutions',
        'CMS integration',
        'API development'
      ],
      originalPrice: '$2,000',
      offerPrice: '$600',
      savings: 'Save 70% | $1,400 off',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      link: '/services/web-development'
    },
    {
      id: 2,
      icon: 'fas fa-mobile-alt',
      title: 'Mobile Development',
      description: 'Cross-platform mobile apps for iOS and Android using React Native and Flutter.',
      features: [
        'iOS & Android apps',
        'Cross-platform development',
        'App store deployment',
        'Push notifications',
        'Offline functionality'
      ],
      originalPrice: '$3,000',
      offerPrice: '$900',
      savings: 'Save 70% | $2,100 off',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      link: '/services/mobile-development'
    },
    {
      id: 3,
      icon: 'fas fa-pencil-ruler',
      title: 'UI/UX Design',
      description: 'User-centered design solutions that create engaging and intuitive experiences.',
      features: [
        'User research',
        'Wireframing',
        'Interactive prototypes',
        'Visual design',
        'Usability testing'
      ],
      originalPrice: '$1,500',
      offerPrice: '$450',
      savings: 'Save 70% | $1,050 off',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      link: '/services/ui-ux-design'
    },
    {
      id: 4,
      icon: 'fas fa-chart-line',
      title: 'Consulting',
      description: 'Expert advice on technology stack, architecture, and best practices.',
      features: [
        'Technical architecture',
        'Code reviews',
        'Performance optimization',
        'Security audits',
        'Team training'
      ],
      originalPrice: '$150/hour',
      offerPrice: '$45/hour',
      savings: 'Save 70% | $105/hour off',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      link: '/services/consulting'
    },
    {
      id: 5,
      icon: 'fas fa-cloud',
      title: 'Cloud Services',
      description: 'Cloud infrastructure setup and management on AWS, Google Cloud, and Azure.',
      features: [
        'Cloud migration',
        'Serverless architecture',
        'Auto-scaling',
        'Load balancing',
        'Monitoring & logging'
      ],
      originalPrice: '$2,500',
      offerPrice: '$750',
      savings: 'Save 70% | $1,750 off',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      link: '/services/cloud'
    },
    {
      id: 6,
      icon: 'fas fa-shield-alt',
      title: 'Cybersecurity',
      description: 'Security assessments and implementation of best security practices.',
      features: [
        'Security audits',
        'Penetration testing',
        'Vulnerability assessment',
        'Security training',
        'Compliance consulting'
      ],
      originalPrice: '$1,800',
      offerPrice: '$540',
      savings: 'Save 70% | $1,260 off',
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      link: '/services/security'
    }
  ];

  const processSteps = [
    {
      icon: 'fas fa-comments',
      title: '1. Consultation',
      description: 'We discuss your requirements, goals, and vision for the project.'
    },
    {
      icon: 'fas fa-pencil-ruler',
      title: '2. Planning',
      description: 'I create a detailed plan, timeline, and cost estimate for your project.'
    },
    {
      icon: 'fas fa-code',
      title: '3. Development',
      description: 'I build your project with regular updates and feedback sessions.'
    },
    {
      icon: 'fas fa-check-circle',
      title: '4. Delivery',
      description: 'Final delivery with testing, deployment, and ongoing support.'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h1 className="services-hero-title">
                <span className="gradient-text">Services</span> I Provide
              </h1>
              <p className="services-hero-subtitle">
                Comprehensive solutions tailored to your needs. From concept to launch, 
                I deliver high-quality work that exceeds expectations.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Grid */}
      <section className="services-grid-section">
        <Container>
          <Row>
            {services.map(service => (
              <Col lg={4} md={6} key={service.id}>
                <Card className="service-card">
                  <div className="service-card-inner">
                    <div className="service-icon-wrapper" style={{ background: service.gradient }}>
                      <i className={service.icon}></i>
                    </div>
                    <CardBody>
                      <h3 className="service-title">{service.title}</h3>
                      <p className="service-description">{service.description}</p>
                      
                      <div className="service-features">
                        {service.features.map((feature, index) => (
                          <div key={index} className="feature-item">
                            <i className="fas fa-check-circle"></i>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      <div className="service-pricing" style={{ '--service-gradient': service.gradient }}>
                        <div className="pricing-topline">
                          <span className="pricing-badge">Limited Offer</span>
                          <span className="pricing-savings">{service.savings}</span>
                        </div>
                        <div className="pricing-values">
                          <div className="pricing-original">
                            <span className="pricing-label">Original Price</span>
                            <span className="pricing-old-value">{service.originalPrice}</span>
                          </div>
                          <div className="pricing-offer">
                            <span className="pricing-label">Offer Price</span>
                            <span className="pricing-new-value">{service.offerPrice}</span>
                          </div>
                        </div>
                      </div>

                      <div className="service-actions">
                        <Link to={service.link} className="btn-service-details">
                          View Details
                          <i className="fas fa-arrow-right"></i>
                        </Link>
                        <Link to="/contact" className="btn-service-consult">
                          <i className="fas fa-paper-plane"></i>
                        </Link>
                      </div>
                    </CardBody>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <Container>
          <h2 className="section-title text-center">
            How I <span className="gradient-text">Work</span>
          </h2>
          <p className="section-subtitle text-center">
            A streamlined process designed to deliver exceptional results
          </p>

          <Row className="process-steps">
            {processSteps.map((step, index) => (
              <Col lg={3} md={6} key={index}>
                <div className="process-step-card">
                  <div className="step-icon-wrapper">
                    <i className={step.icon}></i>
                    <div className="step-line"></div>
                  </div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <Container>
          <Row>
            <Col lg={10} className="mx-auto">
              <h2 className="section-title text-center">
                Frequently Asked <span className="gradient-text">Questions</span>
              </h2>
              
              <div className="faq-grid">
                <div className="faq-item">
                  <h3>How long does a typical project take?</h3>
                  <p>Project timelines vary depending on complexity. A simple website might take 2-4 weeks, while complex applications can take 2-3 months. I'll provide a detailed timeline during our consultation.</p>
                </div>
                <div className="faq-item">
                  <h3>Do you provide maintenance after launch?</h3>
                  <p>Yes, I offer ongoing maintenance and support packages to ensure your project stays secure and up-to-date. We can discuss the best option for your needs.</p>
                </div>
                <div className="faq-item">
                  <h3>What is your payment structure?</h3>
                  <p>Typically, I work with a 50% deposit to start, 25% at milestone completion, and 25% upon final delivery. For larger projects, we can arrange a custom payment schedule.</p>
                </div>
                <div className="faq-item">
                  <h3>Do you work with international clients?</h3>
                  <p>Absolutely! I've worked with clients worldwide. We can communicate via video calls, email, or any platform you prefer.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h2 className="cta-title">Ready to Start Your Project?</h2>
              <p className="cta-description">
                Let's discuss how I can help bring your ideas to life. 
                Get in touch for a free consultation.
              </p>
              <Link to="/contact" className="btn-cta">
                <i className="fas fa-paper-plane me-2"></i>
                Get in Touch
              </Link>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Services;
